import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import SkusTooltip from 'ravenjs/components/tooltips/SkusTooltip';
import Divider from 'ravenjs/lib/Divider';
import Row from 'ravenjs/lib/Row';
import Typography from 'ravenjs/lib/Typography';
import { callFunc, get, getHandler, isEmpty } from 'ravenjs/utils';
import { CheckboxEmptyIconNew, CheckboxFilledIconNew } from 'ravenjs/lib/SvgIcon';
import { generateLicenseName } from 'utils/skus';

import MESSAGES from 'constants/messages';
import Checkbox from '../../CheckboxNew';
import PremiumSolutions from './PremiumSolutions';

const CompanyConfigInput = styled(Checkbox)`
    ${({ $alignTop }) =>
        $alignTop &&
        css`
            margin-top: 14px;
        `};
`;
const SkuTooltipContainer = styled.div`
    ${({ $fill }) =>
        $fill &&
        css`
            flex: 1;
            min-width: 0;
            padding-left: 10px;
        `};
`;
/**
 * A checkbox form field for selecting a new configuration for a company / client.
 *
 * @method      CompanyConfigField
 * @param       {Object}           props Component props
 * @constructor
 */
function CompanyConfigField(props) {
    // Extract a few props.
    const { disabled, formContext, idSchema, onChange, readonly, schema, uiSchema } = props;
    const { handlers = {} } = formContext;
    const defaultVal = get(schema, 'default');
    // `use`Hooks
    const [checkedState, setCheckedState] = React.useState(defaultVal);
    // Build some internally used props.
    const id = get(idSchema, '$id', 'CompanyConfigField');
    const onPickRef = get(uiSchema, 'onPick');
    const configInfo = get(uiSchema, 'configInfo', {});
    const addonConfigInfo = get(uiSchema, 'addonConfigInfo');
    const onPickHandler = getHandler(handlers, onPickRef);
    const configName = get(configInfo, 'configurationName', 'Configuration Name');
    const addonConfigName = get(addonConfigInfo, 'configurationName', 'Add-On Configuration Name');
    const configType = get(configInfo, 'configurationType');
    const configTypeLabel =
        configType === 'master' ? '(Master)' : configType === 'alternate' ? '(Alternate)' : null;
    const configLicense = get(configInfo, 'licenses', []);
    const formattedLicenseInfo = generateLicenseName(configLicense);
    const addonConfigLicense = get(addonConfigInfo, 'licenses', []);
    const formattedAddonLicenseInfo = generateLicenseName(addonConfigLicense);
    const premiumSolutions = get(uiSchema, 'premiumSolutions');
    const hasPremiumSolutions = !isEmpty(premiumSolutions);
    const configTextGutter = hasPremiumSolutions ? '0' : '10px';
    // Handle the `onChange` event of the checkbox input.
    async function handleOnChange(e) {
        const newVal = e.target.checked;
        // Update the local state.
        setCheckedState(newVal);
        // Call the form's onChange func.
        callFunc(onChange, newVal);
        // Call the passed in `onPick` func.
        await callFunc(onPickHandler, newVal, e);
        // Reset the local state back to checked.
        setCheckedState(!newVal);
    }
    // Render info about the add-on config.
    function renderAddonConfigInfo() {
        // If no add-on config info is present, no need to render anything.
        if (isEmpty(addonConfigInfo)) {
            return null;
        }
        // Otherwise, render the sku tooltip info about the add-on config.
        return (
            <>
                <Divider transparent />
                <Row alignItems="center" justify="flex-start" gutter={false}>
                    <CompanyConfigInput
                        checked
                        isDisabled
                        onChange={() => {}}
                        type="checkbox"
                        icon={CheckboxEmptyIconNew}
                        checkedIcon={CheckboxFilledIconNew}
                        id={id}
                    />
                    <SkuTooltipContainer>
                        <SkusTooltip configuration={addonConfigInfo} placement="top-start">
                            <Typography as="span" gutterBottom="0" gutterLeft="10px">
                                {addonConfigName} (Add-On)
                            </Typography>
                        </SkusTooltip>
                        <Typography as="span" gutterBottom="0" gutterLeft="10px">
                            {MESSAGES.LICENSING.CONFIGURATION.LICENSE}: {formattedAddonLicenseInfo}
                        </Typography>
                    </SkuTooltipContainer>
                </Row>
            </>
        );
    }

    // Build the Company Config field.
    return (
        <Row gutter={false} direction="column">
            <Row
                alignItems={hasPremiumSolutions ? 'flex-start' : 'center'}
                justify="flex-start"
                gutter={false}
            >
                <CompanyConfigInput
                    $alignTop={hasPremiumSolutions}
                    icon={CheckboxEmptyIconNew}
                    checkedIcon={CheckboxFilledIconNew}
                    checked={checkedState}
                    isDisabled={disabled || readonly}
                    id={id}
                    onChange={handleOnChange}
                    type="checkbox"
                />
                <SkuTooltipContainer $fill={hasPremiumSolutions}>
                    <SkusTooltip configuration={configInfo} placement="top-start">
                        <Typography as="span" gutterBottom="0" gutterLeft={configTextGutter}>
                            {configName} {configTypeLabel}
                        </Typography>
                    </SkusTooltip>
                    <Typography as="span" gutterBottom="0" gutterLeft={configTextGutter}>
                        {MESSAGES.LICENSING.CONFIGURATION.LICENSE}: {formattedLicenseInfo}
                    </Typography>
                    {hasPremiumSolutions && (
                        <PremiumSolutions
                            disabled={disabled || readonly}
                            formContext={formContext}
                            margin="10px 0 0"
                            premiumSolutions={premiumSolutions}
                        />
                    )}
                </SkuTooltipContainer>
            </Row>
            {renderAddonConfigInfo()}
        </Row>
    );
}

CompanyConfigField.propTypes = {
    disabled: PropTypes.bool,
    formContext: PropTypes.object.isRequired,
    idSchema: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    readonly: PropTypes.bool,
    schema: PropTypes.object.isRequired,
    uiSchema: PropTypes.object.isRequired,
};

CompanyConfigField.defaultProps = {
    disabled: null,
    readonly: null,
};

export default CompanyConfigField;
